import React, { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, ScrollView, KeyboardAvoidingView, Platform, TouchableOpacity } from 'react-native';
import { useLocalSearchParams, useRouter, Stack } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { api } from '../../../src/api/client';
import { useToast } from '../../../src/components/Toast';
import Input from '../../../src/components/Input';
import Button from '../../../src/components/Button';
import ContentContainer from '../../../src/components/ContentContainer';
import BayEquipmentConfig, { type BayEquipmentConfigHandle } from '../../../src/components/bay/BayEquipmentConfig';
import { useTheme } from '../../../src/context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../src/utils/theme';
import type { ColorTheme } from '../../../src/utils/theme';

/** EditVendingScreen - modify a vending machine's config. */
export default function EditVendingScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const { machineId } = useLocalSearchParams<{ machineId: string }>();
  const [name, setName] = useState('');
  const [creditStepCents, setCreditStepCents] = useState('25');
  const [maxCredit, setMaxCredit] = useState('');
  const [presets, setPresets] = useState<string[]>([]);
  const [isActive, setIsActive] = useState(true);
  const [locationId, setLocationId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const router = useRouter();
  const toast = useToast();

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        const m = await api<any>(`/api/vending-machines/${machineId}`);
        if (ac.signal.aborted) return;
        setName(m.name);
        setCreditStepCents(String(m.creditStepCents ?? 25));
        setMaxCredit(((m.maxCreditCents ?? 0) / 100).toFixed(2));
        setIsActive(m.isActive !== false);
        setLocationId(m.locationId);
        if (m.presets) setPresets(m.presets.map((p: number) => (p / 100).toFixed(2)));
      } catch (err: any) {
        if (ac.signal.aborted) return;
        toast.show({ type: 'error', title: 'Error', message: err.message });
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [machineId]);

  const equipRef = useRef<BayEquipmentConfigHandle>(null);
  const handleSave = async () => {
    if (!name.trim()) { toast.show({ type: 'error', title: 'Machine name is required' }); return; }
    const step = parseInt(creditStepCents) || 25;
    if (step < 5 || step > 500) { toast.show({ type: 'error', title: 'Credit step must be 5–500 cents' }); return; }
    const maxCents = Math.round(parseFloat(maxCredit) * 100);
    if (!maxCents || maxCents < 25) { toast.show({ type: 'error', title: 'Enter a valid maximum credit (at least $0.25)' }); return; }
    const validPresets = presets
      .filter(p => p && parseFloat(p) > 0)
      .map(p => Math.round(parseFloat(p) * 100));
    const badPreset = validPresets.find(p => p > maxCents || p % step !== 0);
    if (badPreset != null) {
      toast.show({ type: 'error', title: `Presets must be multiples of ${step}¢ and at most the maximum credit` });
      return;
    }

    const body: any = {
      name: name.trim(),
      creditStepCents: step,
      maxCreditCents: maxCents,
      presets: validPresets.length > 0 ? validPresets : undefined,
      isActive,
    };

    setSaving(true);
    try {
      await api(`/api/vending-machines/${machineId}`, { method: 'PUT', body });
      if (equipRef.current) { const ok = await equipRef.current.save(); if (!ok) { setSaving(false); return; } }
      toast.show({ type: 'success', title: 'Equipment updated' });
      router.replace(`/owner/location/${locationId}` as never);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <View style={styles.container}><Text style={styles.loadingText}>Loading...</Text></View>;

  return (
    <>
      <Stack.Screen options={{ title: 'Edit Equipment' }} />
      <KeyboardAvoidingView style={styles.container} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView contentContainerStyle={styles.scroll} keyboardShouldPersistTaps="handled">
          <ContentContainer>
          <Input label="Machine Name" value={name} onChangeText={setName} icon="fast-food-outline" maxLength={48} />

          <View style={styles.activeRow}>
            <Text style={styles.fieldLabel}>Active</Text>
            <TouchableOpacity onPress={() => setIsActive(!isActive)} style={[styles.toggle, isActive && styles.toggleActive]}>
              <Text style={[styles.toggleText, isActive && styles.toggleTextActive]}>{isActive ? 'Yes' : 'No'}</Text>
            </TouchableOpacity>
          </View>

          <Input label="Maximum Credit ($)" value={maxCredit} onChangeText={(v: string) => setMaxCredit(v.replace(/[^0-9.]/g, ''))} keyboardType="decimal-pad" icon="trending-up-outline" />

          <Text style={styles.fieldLabel}>Credit amounts</Text>
          <Text style={styles.helpText}>
            Up to 6 amounts customers can buy; each delivers the same amount in credit. If empty, customers see $1 / $2 / $3.
          </Text>
          <View style={styles.presetsWrap}>
            {presets.map((p, i) => (
              <View key={i} style={styles.presetRow}>
                <View style={{ flex: 1 }}>
                  <Input placeholder="$ amount" value={p} onChangeText={(v: string) => { const u = [...presets]; u[i] = v.replace(/[^0-9.]/g, ''); setPresets(u); }} keyboardType="decimal-pad" />
                </View>
                <TouchableOpacity onPress={() => setPresets(presets.filter((_, idx) => idx !== i))} style={styles.removeRow}>
                  <Ionicons name="close-circle" size={24} color={colors.danger} />
                </TouchableOpacity>
              </View>
            ))}
            {presets.length < 6 && (
              <TouchableOpacity onPress={() => setPresets([...presets, ''])} style={styles.addRowBtn}>
                <Ionicons name="add-circle-outline" size={20} color={colors.primary} />
                <Text style={styles.addRowText}>Add Amount</Text>
              </TouchableOpacity>
            )}
          </View>

          <Input label="Credit per pulse (cents)" value={creditStepCents} onChangeText={(v: string) => setCreditStepCents(v.replace(/[^0-9]/g, ''))} keyboardType="number-pad" icon="cash-outline" />
          <Text style={styles.helpText}>What one relay pulse is worth on the machine's coin acceptor (25 for most).</Text>

          <Text style={styles.sectionTitle}>Controller Setup</Text>
          <BayEquipmentConfig ref={equipRef} bayId={machineId!} bayType="vending" apiBase={`/api/vending-machines/${machineId}`} hideSave />

          <Button title="Save Changes" onPress={handleSave} loading={saving} size="lg" style={styles.saveBtn} />
          <Button title="Cancel" onPress={() => router.back()} variant="outline" size="md" style={styles.cancelBtn} />
          </ContentContainer>
        </ScrollView>
      </KeyboardAvoidingView>
    </>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  scroll: { padding: spacing.lg },
  loadingText: { fontSize: fontSize.md, color: c.textSecondary, textAlign: 'center', marginTop: spacing.xxl },
  fieldLabel: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text, marginBottom: spacing.sm },
  helpText: { fontSize: fontSize.xs, color: c.textLight, marginTop: -spacing.sm, marginBottom: spacing.md },
  row: { flexDirection: 'row', gap: spacing.sm },
  activeRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.lg },
  toggle: { paddingHorizontal: spacing.lg, paddingVertical: spacing.sm, borderRadius: borderRadius.md, backgroundColor: c.surfaceSecondary, borderWidth: 1, borderColor: c.border },
  toggleActive: { backgroundColor: c.primary + '15', borderColor: c.primary },
  toggleText: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.textSecondary },
  toggleTextActive: { color: c.primary },
  presetsWrap: { marginBottom: spacing.sm },
  presetRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  removeRow: { paddingBottom: spacing.md },
  addRowBtn: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, paddingVertical: spacing.sm },
  addRowText: { fontSize: fontSize.sm, color: c.primaryDark, fontWeight: fontWeight.semibold },
  pulseSection: { marginTop: spacing.lg, paddingTop: spacing.lg, borderTopWidth: 1, borderTopColor: c.border },
  advancedCard: { marginBottom: spacing.md },
  sectionTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.xs },
  saveBtn: { marginTop: spacing.lg },
  cancelBtn: { marginTop: spacing.sm, marginBottom: spacing.xxl },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
